import { Head, Link } from "@inertiajs/react"
import { useState, useRef, useEffect } from "react"
import DashboardLayout from "@/components/dashboard/layout"
import ListingForm from "@/components/dashboard/listing-form"
import UrlImportForm from "@/components/dashboard/listings/url-import-form"
import { Button } from "@/components/ui/button"

export default function NewListing() {
  const [showManual, setShowManual] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get("manual") === "1"
  })
  const manualRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (showManual && manualRef.current) {
      manualRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [showManual])

  return (
    <DashboardLayout>
      <Head title="Add listing | QuiteRight" />

      {/* Back link */}
      <Link
        href="/dashboard/listings"
        className="mb-8 inline-flex items-center gap-1.5 text-sm text-warm-grey hover:text-charcoal transition-colors"
      >
        <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
        </svg>
        Listings
      </Link>

      {/* Hero import section */}
      <div className="rounded-2xl border border-stone/40 bg-white px-6 py-12 sm:px-12 sm:py-16">
        <div className="mx-auto max-w-xl text-center">
          {/* Icon cluster — three stacked property outlines */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="absolute -left-3 -top-1.5 size-12 rounded-lg border-2 border-stone/30 bg-cream/80 rotate-[-6deg]" />
              <div className="absolute -right-2 -top-1 size-12 rounded-lg border-2 border-stone/40 bg-stone/20 rotate-[4deg]" />
              <div className="relative flex size-14 items-center justify-center rounded-xl border-2 border-red/30 bg-red/5">
                <svg className="size-7 text-red" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                </svg>
              </div>
            </div>
          </div>

          <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal sm:text-4xl">
            Add a listing
          </h1>
          <p className="mt-3 text-warm-grey leading-relaxed">
            Paste a property URL and we&apos;ll do the rest. Works with Rightmove,
            Zoopla, OnTheMarket, Airbnb, and more.
          </p>

          {/* URL input */}
          <UrlImportForm />

          {/* Supported sites hint */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] font-medium tracking-wide uppercase text-warm-grey/50">
            <span>Rightmove</span>
            <span className="text-stone">·</span>
            <span>Zoopla</span>
            <span className="text-stone">·</span>
            <span>OnTheMarket</span>
            <span className="text-stone">·</span>
            <span>Airbnb</span>
            <span className="text-stone">·</span>
            <span>Booking.com</span>
          </div>
        </div>
      </div>

      {/* Manual entry toggle */}
      <div className="mt-6 text-center">
        <Button
          variant="secondary"
          onClick={() => setShowManual(!showManual)}
        >
          <svg className="mr-1.5 size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
          </svg>
          {showManual ? "Hide manual form" : "Or add details manually"}
        </Button>
      </div>

      {/* Manual form — revealed on click */}
      <div
        ref={manualRef}
        className={`grid transition-all duration-300 ease-in-out ${
          showManual
            ? "mt-8 grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <ListingForm action="create" />
        </div>
      </div>
    </DashboardLayout>
  )
}
